import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { Autocomplete } from "../../../components/Autocomplete";

import { useStyles } from "./styles";
import * as Yup from "yup";

import EmptyImage from "../../../assets/initialSettings/storeSettings/EmptyImage.svg";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { useFormik } from "formik";
import { InputSettingsStore } from "../../../types/storeSettings";
import { useEffect, useMemo, useState } from "react";
import { TextField } from "../components/TextField";
import { BLACK } from "../../../utils/colors";
import { useToast } from "../../../hooks/useToast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  listCompany,
  listSegments,
  putSegment,
} from "../../../services/company";
import { Company, SegmentCompany } from "../../../types/companies";
import { ConvertedBlob, convertToBase64 } from "../../../utils/base64";
import { Store } from "../../../types/stores";
import { createStoreLogo, updateStore } from "../../../services/stores";
import { isValidURL } from "../../../utils/validations";
import { STORE_NAME_PATTERN } from "..";
import { Alert, Checkbox, CircularProgress, Popover } from "@mui/material";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useCompanySegment } from "../../../hooks/useCompanySegment";

const STORE_SETTINGS_VALIDATION = Yup.object().shape({
  segment: Yup.object()
    .shape({
      id: Yup.number(),
      description: Yup.string(),
      is_active: Yup.number(),
      createdAt: Yup.date(),
      updatedAt: Yup.date(),
      deletedAt: Yup.date().nullable(),
    })
    .nullable()
    .required("Este campo é obrigatório"),
  base64: Yup.string().nullable(),
  store_name: Yup.string()
    .nullable()
    .min(2, "O nome da loja deve deve possuir pelo menos 2 caracteres")
    .matches(
      /^\s*\S.*$/,
      "Este campo não pode conter somente espaços em branco"
    ),
  whatsapp: Yup.string().nullable(),
  cep: Yup.string().nullable(),
  uf: Yup.string().nullable(),
  address: Yup.string().nullable(),
  district: Yup.string().nullable(),
  city: Yup.string().nullable(),
});

type StoreSettingsProps = {
  store: Store;
  loadingStore: boolean;
  onAdvance: () => void;
};

export function StoreSettings({
  store,
  loadingStore,
  onAdvance,
}: StoreSettingsProps) {
  const styles = useStyles();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const companySegment = useCompanySegment();

  // const [base64, setBase64] = useState<string | null>(null);
  const [isFilled, setIsFilled] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const {
    data: dataSegments,
    isLoading: loadingSegments,
    isSuccess,
  } = useQuery("listSegments", async () => {
    const segments: any = await listSegments();
    return segments;
  });

  const { data: dataCompany, isLoading: loadingCompany } = useQuery(
    "getCompany",
    async () => {
      const company = await listCompany();

      return company;
    }
  );

  const segments = useMemo(
    (): SegmentCompany[] => (dataSegments ? dataSegments.data?.content : []),
    [dataSegments]
  );

  const company = useMemo(
    (): Company => (dataCompany ? dataCompany.data : null),
    [dataCompany]
  );

  useEffect(() => {
    const params = {
      addAccount: "Store",
    };
    const options = {
      pathname: location.pathname,
      search: `?${createSearchParams(params)}`,
    };

    navigate(options, { replace: true });
  }, [location.pathname, navigate]);

  const segmentInCompanyMutation = useMutation(
    async (id: number) => {
      const response = await putSegment(id);

      return response;
    },
    {
      onSuccess: () => {
        if (!isFilled) {
          queryClient.invalidateQueries("getCompanySegment");

          toast.show(`Seu segmento foi salvado com sucesso!`, {
            severity: "success",
          });
        }
      },
      onError: (err) => {
        toast.show(
          "Não foi possível adicionar um segmento na sua empresa. Por favor, tente novamente ou entre em contato com o suporte",
          { severity: "warning" }
        );
      },
    }
  );

  const createStoreLogoMutation = useMutation(createStoreLogo, {
    onError: (err) => {
      toast.show(`Ocorreu um erro na criação da imagem!`, {
        severity: "error",
      });
    },
  });

  const updateStoreMutation = useMutation(
    async (store: Store) => {
      const response = await updateStore(store);
      return response;
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries("getCompanySegment");

        toast.show(`Sua loja foi criada com sucesso!`, {
          severity: "success",
        });
      },
      onError: (err) => {
        console.log("delete store", err);
      },
    }
  );

  const findCompanySegment = useMemo(() => {
    if (companySegment && segments.length > 0) {
      return (
        segments.find((segment) => segment.id === companySegment?.segment) ||
        null
      );
    }

    return null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySegment, segments]);

  const INITIAL_FORMIK_DATA = useMemo(() => {
    return {
      segment: findCompanySegment,
      cnpj: company?.cnpj || "",
      store_name: store.nome_loja === STORE_NAME_PATTERN ? "" : store.nome_loja,
      whatsapp: store.whatsapp_phone_number || "",
      cep: store.cep || "",
      uf: store.uf || "",
      address: store.endereco || "",
      district: store.bairro || "",
      city: store.cidade || "",
      base64: store.logo1 || "",
      is_not_remove_background: true,
    };
  }, [store, company, findCompanySegment]);

  const handleSubmit = async (event: InputSettingsStore) => {
    if (!formik.isValid) {
      return;
    }

    try {
      await segmentInCompanyMutation.mutateAsync(event.segment!.id);

      if (isFilled) {
        const factoryEditStore: Store = {
          id_loja: store.id_loja,
          nome_loja: event.store_name || store.nome_loja,
          bairro: event.district!,
          cep: event.cep!,
          cidade: event.city!,
          endereco: event.address!,
          uf: event.uf!,
          whatsapp_phone_number: event.whatsapp!,
          logo1: event.base64!,
          naoRemoverFundo: event.is_not_remove_background,
        };

        let newImage = "";

        if (
          Boolean(factoryEditStore.logo1) &&
          /;base64,/g.test(factoryEditStore.logo1!)
        ) {
          newImage = await createStoreLogoMutation.mutateAsync({
            imagem_produto: factoryEditStore.logo1,
            isBase64: true,
            naoRemoverFundo: Boolean(factoryEditStore.naoRemoverFundo),
            isLogo: true,
          });
        }

        let dateNow: Date | string = new window.Date();
        dateNow = dateNow.getTime().toString();

        if (isValidURL(newImage)) {
          newImage = newImage.split("?")[0] + "?" + dateNow;
        }

        factoryEditStore.logo1 = newImage;

        await updateStoreMutation.mutateAsync(factoryEditStore);
      }
    } catch (err) {
      toast.show(
        "Não foi possível criar a sua loja. Por favor, entre em contato com o suporte.",
        { severity: "warning" }
      );
    }

    onAdvance();
  };

  const handleClose = () => {
    // setBase64("");
    formik.setFieldValue("base64", "");
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const isLogoSizeTooBig = (img: ConvertedBlob): boolean => {
    const maxSize = 2 * 10 ** 6; // 2 Mb

    if (img.size > maxSize) {
      return true;
    }

    return false;
  };

  const formik = useFormik({
    //InputSettingsStore
    initialValues: INITIAL_FORMIK_DATA,
    validationSchema: STORE_SETTINGS_VALIDATION,
    isInitialValid: false,
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: false,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    const { store_name, base64, whatsapp, cep, uf, address, district, city } =
      formik.values;

    if (
      store_name ||
      base64 ||
      whatsapp ||
      cep ||
      uf ||
      address ||
      district ||
      city
    ) {
      setIsFilled(true);

      return;
    }

    setIsFilled(false);
  }, [formik.values, isFilled]);

  useEffect(() => {
    if (companySegment && segments.length > 0 && !formik.values.segment) {
      const findCompanySegment =
        segments.find((segment) => segment.id === companySegment?.segment) ||
        null;

      if (store && company) {
        formik.setValues({
          //@ts-ignore
          segment: findCompanySegment,
          cnpj: company?.cnpj || "",
          store_name:
            store.nome_loja === STORE_NAME_PATTERN ? "" : store.nome_loja,
          whatsapp: store.whatsapp_phone_number || "",
          cep: store.cep || "",
          uf: store.uf || "",
          address: store.endereco || "",
          district: store.bairro || "",
          city: store.cidade || "",
          base64: store.logo1 || "",
          is_not_remove_background: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, company, companySegment?.segment, segments]);

  const isLoadingButtonSubmit =
    loadingCompany ||
    loadingSegments ||
    loadingStore ||
    formik.isSubmitting ||
    segmentInCompanyMutation.isLoading ||
    createStoreLogoMutation.isLoading ||
    updateStoreMutation.isLoading;

  return (
    <Box className={styles.container}>
      <Box className={styles.titleBar}>
        <Typography
          variant="h3"
          fontSize={24}
          fontWeight={600}
          sx={{
            color: BLACK,
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textAlign: "left",
            display: "block",
          }}
        >
          Configure sua empresa
        </Typography>
      </Box>
      <Link
        href={"https://www.youtube.com/watch?v=zYz4kF_qTtc"}
        target={"_blank"}
        underline="none"
      >
        <Button
          variant="contained"
          sx={{
            color: "#ffffff",
            backgroundColor: "#0C20B5",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textAlign: "left",
            display: "block",
            marginBottom: "32px",
          }}
        >
          Como configurar esta página?
        </Button>
      </Link>
      <Divider />

      {isSuccess && !loadingSegments ? (
        <form onSubmit={formik.handleSubmit}>
          <Box>
            <Autocomplete
              selectOnFocus={true}
              isOpaque={true}
              disabled={loadingSegments}
              options={segments}
              loading={loadingSegments}
              getOptionLabel={(option: SegmentCompany) => option.description}
              label="Qual é o seu segmento?"
              value={formik.values.segment}
              error={!!formik.errors.segment && !formik.values.segment}
              onChange={(e, value) => {
                formik.setFieldValue("segment", value);
              }}
              renderOption={(props, option: SegmentCompany) => {
                return (
                  <li {...props} key={option.id}>
                    {option.description}
                  </li>
                );
              }}
              helperText={
                !!formik.errors.segment && !formik.values.segment
                  ? "Este campo é obrigatório."
                  : ""
              }
              endAdornment={
                loadingSegments ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null
              }
              sx={{ margin: "32px 0 32px 0" }}
            />

            <TextField
              label="CNPJ"
              value={formik.values.cnpj}
              disabled={true}
              placeholder={"Escolha o segmento da sua empresa"}
              helperText={
                "Este campo não é editável. Em caso de divergência, acione nossa equipe."
              }
              endAdornment={
                loadingCompany ? (
                  <CircularProgress color="inherit" size={20} />
                ) : undefined
              }
              sx={{ marginBottom: "32px", width: "100%" }}
            />
          </Box>

          <Divider />

          <Box display={"flex"} flexDirection={"column"} marginTop={4}>
            <Typography className={styles.storeTitle}>
              Informações da loja
            </Typography>
            <Typography className={styles.storeDescription}>
              É possível adicionar outras lojas na aba de{" "}
              <strong>Configurações</strong> futuramente.
            </Typography>

            <Box display="flex">
              <label htmlFor="contained-button-file">
                <Box className={styles.imageContainer}>
                  <img
                    src={
                      formik.values.base64 ? formik.values.base64 : EmptyImage
                    }
                    alt={"Caixa para inserir a logo da empresa a ser salva"}
                    className={styles.inputLogo}
                    style={{
                      // @ts-ignore:next-line
                      width: formik.values.base64 ? "100%" : "50%",
                      // @ts-ignore:next-line
                      maxHeight: formik.values.base64 ? "100%" : "50%",
                      objectFit: "contain",
                    }}
                  />
                  {!formik.values.base64 ? (
                    <Typography className={styles.imageDescription}>
                      Adicionar logo
                    </Typography>
                  ) : null}
                  <input
                    accept="image/jpeg, image/png"
                    style={{ display: "none" }}
                    id="contained-button-file"
                    type="file"
                    value={""}
                    onChange={async (e) => {
                      const convertedImage = await convertToBase64(
                        e.target.files![0]
                      );

                      console.log("convertedImage", convertedImage);
                      // setBase64(convertedImage.base64 as string);
                      if (!isLogoSizeTooBig(convertedImage)) {
                        formik.setFieldValue("base64", convertedImage.base64);
                      }
                    }}
                  />
                </Box>
              </label>

              <Box className={styles.containerImageOptions}>
                {formik.values.base64 && (
                  <>
                    <Box
                      className={styles.deleteContainer}
                      onClick={handleClose}
                    >
                      <DeleteIcon color="error" />
                    </Box>

                    <Box
                      // display={"flex"}
                      className={styles.removeBackgroundOption}
                    >
                      <Checkbox
                        id="checkbox_remove_background"
                        value={formik.values.is_not_remove_background}
                        // checked={formik.values.is_not_remove_background}
                        onChange={({ target }) => {
                          formik.setFieldValue(
                            "is_not_remove_background",
                            !target.checked
                          );
                        }}
                        name="Remover fundo da imagem"
                        size={"small"}
                      />{" "}
                      <label htmlFor="checkbox_remove_background">
                        <Typography
                          sx={{ cursor: "pointer", fontSize: "12px" }}
                        >
                          Remover fundo da imagem
                        </Typography>
                      </label>
                      <Box
                        onMouseEnter={handlePopoverOpen}
                        onMouseLeave={handlePopoverClose}
                        sx={{ marginLeft: "4px" }}
                      >
                        <HelpOutlineIcon fontSize="small" color="info" />
                      </Box>
                      <Popover
                        open={!!anchorEl}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        onClose={handlePopoverClose}
                        disableRestoreFocus
                        sx={{
                          pointerEvents: "none",
                        }}
                      >
                        <Box>
                          <Alert
                            severity="info"
                            className={styles.alertRemoveBackground}
                          >
                            {" "}
                            O fundo será removido ao clicar no botão de
                            "Continuar"{" "}
                          </Alert>
                        </Box>
                      </Popover>
                    </Box>
                  </>
                )}

                {!formik.values.base64 && (
                  <>
                    <Box
                    // className={styles.deleteContainer}
                    // onClick={handleClose}
                    >
                      <Typography fontSize={13}>
                        O tamanho da imagem não pode ultrapassar <br />2 Mb.
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>

            <Box display="flex" flexDirection="column">
              <TextField
                label="Nome da loja"
                value={formik.values.store_name}
                placeholder="Loja 1"
                className={styles.storeInformations}
                error={!!formik.errors.store_name}
                helperText={
                  !!formik.errors.store_name
                    ? formik.errors.store_name
                    : undefined
                }
                onChange={(event: any) => {
                  formik.setFieldValue("store_name", event.target.value);
                }}
              />
              <TextField
                label="Whatsapp da loja"
                value={formik.values.whatsapp}
                className={styles.storeInformations}
                error={!!formik.errors.whatsapp}
                onChange={(event: any) =>
                  formik.setFieldValue("whatsapp", event.target.value)
                }
              />

              <Box display={"flex"} justifyContent="space-between">
                <TextField
                  label="CEP"
                  value={formik.values.cep}
                  className={`${styles.storeInformations} ${styles.inputSameLine}`}
                  error={!!formik.errors.cep}
                  onChange={(event: any) =>
                    formik.setFieldValue("cep", event.target.value)
                  }
                  sx={{ marginRight: "16px" }}
                />
                <TextField
                  label="UF"
                  value={formik.values.uf}
                  className={`${styles.storeInformations} ${styles.inputSameLine}`}
                  error={!!formik.errors.uf}
                  onChange={(event: any) =>
                    formik.setFieldValue("uf", event.target.value)
                  }
                />
              </Box>

              <TextField
                label="Endereço"
                value={formik.values.address}
                className={styles.storeInformations}
                error={!!formik.errors.address}
                onChange={(event: any) =>
                  formik.setFieldValue("address", event.target.value)
                }
              />
              <TextField
                label="Bairro"
                value={formik.values.district}
                className={styles.storeInformations}
                error={!!formik.errors.district}
                onChange={(event: any) =>
                  formik.setFieldValue("district", event.target.value)
                }
              />
              <TextField
                label="Cidade"
                value={formik.values.city}
                error={!!formik.errors.city}
                onChange={(event: any) =>
                  formik.setFieldValue("city", event.target.value)
                }
                sx={{ marginBottom: "16px" }}
              />

              {!!formik.errors.segment && (
                <Box className={styles.containerMessageError}>
                  <Typography fontSize={14}>
                    O campo de segmento é obrigatório. Preencha-o, por favor!
                  </Typography>
                </Box>
              )}

              {!!formik.errors.store_name && (
                <Box className={styles.containerMessageError}>
                  <Typography fontSize={14}>
                    O campo de nome da loja é obrigatório. Preencha-o, por
                    favor!
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />

            <Button
              variant="contained"
              type="submit"
              disabled={
                !!formik.errors.segment ||
                !!formik.errors.store_name ||
                isLoadingButtonSubmit
              }
              // onClick={() => {
              //   console.log("isLoadingButtonSubmit", isLoadingButtonSubmit);
              //   if (!isLoadingButtonSubmit) {
              //     window.scrollTo(0, 0);
              //   }
              // }}
              sx={{
                alignSelf: "flex-end",
                marginTop: "32px",
                cursor: !!formik.errors.segment ? "not-allowed" : "pointer",
              }}
            >
              {isLoadingButtonSubmit ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                "Continuar"
              )}
            </Button>
          </Box>
        </form>
      ) : (
        <Box className={styles.boxCircular}>
          <CircularProgress variant="indeterminate" />
        </Box>
      )}
    </Box>
  );
}
